/**
 * Structured root-cause capture for a ticket.
 *
 * The reason-code taxonomy exists for every ticket type the backend
 * classifies — SLA breaches, customer delays, cancellations, refunds and
 * shrinkage — but this card used to render for `sla_breach` only, so a
 * cancellation ticket had no way to record *why* it happened even though
 * the codes were sitting there. It is now driven by the ticket's own type.
 *
 * Cascade rules:
 *
 *   Category   = first dropdown   = root reasons (`parent_id` IS NULL)
 *                for the ticket's reason category
 *   Reason     = second dropdown  = children of the chosen category
 *   Sub-reason = third dropdown   = children of the chosen reason
 *                (disabled when a reason has no leaf children)
 *
 * The picker is purely client-side: we rely on `useCrmReasonCodes(category)`
 * which returns the whole taxonomy for that category in one call (the tree
 * is small) and partition it locally by `parent_id`. Saving fires a single
 * PATCH so the backend can validate the chosen ids form a valid path.
 */
import { useEffect, useMemo, useState } from 'react';
import { ClipboardList, Loader2, Save, Undo2, Wand2 } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { Field } from '@/components/ui/Field';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import {
  useCrmReasonCodes,
  usePatchCrmTicket,
  type CrmReason,
  type CrmReasonCategory,
  type CrmTicket,
  type CrmTicketAvoidable,
  type CrmTicketType,
} from '@/api/bi/crm';
import { TICKET_TYPE_LABEL } from '@/lib/crmStatus';
import { apiErrorMessage } from '@/lib/apiError';
import { notify } from '@/lib/toast';
import { cn } from '@/lib/cn';
import type { TicketCapabilities } from '@/features/crm/useTicketCapabilities';

interface Props {
  ticket: CrmTicket;
  capabilities: TicketCapabilities;
}

interface Draft {
  categoryId: number | null;
  reasonId: number | null;
  subReasonId: number | null;
  isAvoidable: CrmTicketAvoidable;
  notes: string;
}

/**
 * Ticket type → reason-code category. `channel_escalation` has no taxonomy,
 * so the card stays hidden for it rather than showing empty dropdowns.
 */
const CATEGORY_BY_TYPE: Record<CrmTicketType, CrmReasonCategory | null> = {
  sla_breach: 'sla_breach',
  customer_delay: 'customer_delay',
  cancellation: 'cancellation',
  refund: 'refund',
  shrinkage: 'shrinkage',
  channel_escalation: null,
  damage: 'damage'
  
};

function dispositionCategoryFor(type: CrmTicketType): CrmReasonCategory | null {
  return CATEGORY_BY_TYPE[type] ?? null;
}

function fromTicket(ticket: CrmTicket): Draft {
  return {
    categoryId: ticket.reason_root?.id ?? null,
    reasonId: ticket.reason_code?.id ?? null,
    subReasonId: ticket.sub_reason_code?.id ?? null,
    isAvoidable: ticket.is_avoidable ?? '',
    notes: ticket.sla_disposition_notes ?? '',
  };
}

/** Radix Select.Item must not use `value=""`; empty string is reserved on the root Select. */
const AVOIDABLE_NOT_SET_SELECT_VALUE = '__not_set__';
/** Lets an operator undo a wrong category/reason instead of being stuck with it. */
const CLEAR_SELECT_VALUE = '__clear__';

const AVOIDABLE_OPTIONS: Array<{
  selectValue: string;
  draftValue: CrmTicketAvoidable;
  label: string;
}> = [
  { selectValue: AVOIDABLE_NOT_SET_SELECT_VALUE, draftValue: '', label: 'Not set' },
  { selectValue: 'yes', draftValue: 'yes', label: 'Avoidable' },
  { selectValue: 'no', draftValue: 'no', label: 'Unavoidable' },
  { selectValue: 'unknown', draftValue: 'unknown', label: 'Unknown' },
];

export function TicketDispositionCard({ ticket, capabilities }: Props) {
  const category = dispositionCategoryFor(ticket.type);
  const reasons = useCrmReasonCodes(category ?? undefined);
  const patch = usePatchCrmTicket(ticket.id);
  const [draft, setDraft] = useState<Draft>(() => fromTicket(ticket));
  const [errors, setErrors] = useState<{ reason?: string; subReason?: string }>({});

  const server = useMemo(() => fromTicket(ticket), [ticket]);
  const dirty = useMemo(
    () => JSON.stringify(server) !== JSON.stringify(draft),
    [draft, server],
  );

  // Only re-seed when the operator has no unsaved work, so a websocket
  // invalidation cannot silently discard a half-filled disposition.
  useEffect(() => {
    if (dirty) return;
    setDraft(server);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    ticket.id,
    server.categoryId,
    server.reasonId,
    server.subReasonId,
    server.isAvoidable,
    server.notes,
  ]);

  const tree = useMemo(() => {
    const all = reasons.data?.results ?? [];
    const roots = all.filter((r) => r.parent_id === null).sort((a, b) => a.order - b.order);
    const byParent = new Map<number, CrmReason[]>();
    for (const r of all) {
      if (r.parent_id != null) {
        const arr = byParent.get(r.parent_id) ?? [];
        arr.push(r);
        byParent.set(r.parent_id, arr);
      }
    }
    for (const [, arr] of byParent) arr.sort((a, b) => a.order - b.order);
    return { roots, byParent };
  }, [reasons.data]);

  const reasonOptions = draft.categoryId ? tree.byParent.get(draft.categoryId) ?? [] : [];
  const subReasonOptions = draft.reasonId ? tree.byParent.get(draft.reasonId) ?? [] : [];
  const reasonHasChildren = subReasonOptions.length > 0;
  const canEdit = capabilities.canEdit;

  const suggestion =
    subReasonOptions.find((r) => r.id === draft.subReasonId)?.is_avoidable_hint ||
    reasonOptions.find((r) => r.id === draft.reasonId)?.is_avoidable_hint ||
    '';

  if (!category) return null;

  function setCategory(value: string) {
    const id = value === CLEAR_SELECT_VALUE || value === '' ? null : Number(value);
    setDraft((d) => ({ ...d, categoryId: id, reasonId: null, subReasonId: null }));
    setErrors({});
  }

  function setReason(value: string) {
    const id = value === CLEAR_SELECT_VALUE || value === '' ? null : Number(value);
    setDraft((d) => ({ ...d, reasonId: id, subReasonId: null }));
    setErrors({});
  }

  function setSubReason(value: string) {
    const id = value === CLEAR_SELECT_VALUE || value === '' ? null : Number(value);
    setDraft((d) => ({ ...d, subReasonId: id }));
    setErrors((e) => ({ ...e, subReason: undefined }));
  }

  function applySuggestion() {
    if (!suggestion) return;
    setDraft((d) => ({ ...d, isAvoidable: suggestion as CrmTicketAvoidable }));
  }

  async function handleSave() {
    const next: typeof errors = {};
    if (draft.categoryId != null && draft.reasonId == null) {
      next.reason = 'Pick a reason under the chosen category.';
    }
    if (reasonHasChildren && draft.subReasonId == null) {
      next.subReason = 'This reason has sub-reasons — pick one so the analytics stay usable.';
    }
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    try {
      await patch.mutateAsync({
        reason_code_id: draft.reasonId,
        sub_reason_code_id: draft.subReasonId,
        is_avoidable: draft.isAvoidable,
        sla_disposition_notes: draft.notes,
      });
      notify.success('Disposition saved.');
    } catch (err) {
      notify.error(apiErrorMessage(err, 'Could not save the disposition.'));
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="inline-flex items-center gap-2">
          <ClipboardList className="h-4 w-4" /> {TICKET_TYPE_LABEL[ticket.type]} disposition
        </CardTitle>
        <div className="inline-flex items-center gap-2">
          {dirty ? <Badge tone="warning">Unsaved</Badge> : null}
          {ticket.is_avoidable ? (
            <Badge
              tone={
                ticket.is_avoidable === 'yes'
                  ? 'warning'
                  : ticket.is_avoidable === 'no'
                    ? 'success'
                    : 'neutral'
              }
            >
              {ticket.is_avoidable === 'yes'
                ? 'Avoidable'
                : ticket.is_avoidable === 'no'
                  ? 'Unavoidable'
                  : 'Unknown'}
            </Badge>
          ) : (
            <Badge tone="neutral">Awaiting disposition</Badge>
          )}
        </div>
      </CardHeader>
      <CardBody className="space-y-3 text-sm">
        <p className="text-[11px] text-muted-foreground">
          Capture the structured root cause. The taxonomy and the avoidability flag drive the
          management dashboards under <em>SLA Disposition Analytics</em>.
        </p>

        <div className="grid gap-3 md:grid-cols-3">
          <Field label="Category" htmlFor={`disp-cat-${ticket.id}`}>
            <Select
              value={draft.categoryId == null ? '' : String(draft.categoryId)}
              onValueChange={setCategory}
              disabled={reasons.isLoading || !canEdit}
            >
              <SelectTrigger id={`disp-cat-${ticket.id}`} className="h-9 text-xs">
                <SelectValue placeholder={reasons.isLoading ? 'Loading…' : 'Select category'} />
              </SelectTrigger>
              <SelectContent>
                {draft.categoryId != null ? (
                  <SelectItem value={CLEAR_SELECT_VALUE}>— Clear —</SelectItem>
                ) : null}
                {tree.roots.map((r) => (
                  <SelectItem key={r.id} value={String(r.id)}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!reasons.isLoading && tree.roots.length === 0 ? (
              <p className="text-[11px] text-muted-foreground">
                No reason codes configured for {TICKET_TYPE_LABEL[ticket.type].toLowerCase()} yet —
                add them under CRM Settings → Reason codes.
              </p>
            ) : null}
          </Field>
          <Field label="Reason" htmlFor={`disp-reason-${ticket.id}`} error={errors.reason}>
            <Select
              value={draft.reasonId == null ? '' : String(draft.reasonId)}
              onValueChange={setReason}
              disabled={!draft.categoryId || !canEdit}
            >
              <SelectTrigger id={`disp-reason-${ticket.id}`} className="h-9 text-xs">
                <SelectValue
                  placeholder={draft.categoryId ? 'Select reason' : 'Pick a category first'}
                />
              </SelectTrigger>
              <SelectContent>
                {draft.reasonId != null ? (
                  <SelectItem value={CLEAR_SELECT_VALUE}>— Clear —</SelectItem>
                ) : null}
                {reasonOptions.map((r) => (
                  <SelectItem key={r.id} value={String(r.id)}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Sub-reason" htmlFor={`disp-sub-${ticket.id}`} error={errors.subReason}>
            <Select
              value={draft.subReasonId == null ? '' : String(draft.subReasonId)}
              onValueChange={setSubReason}
              disabled={!reasonHasChildren || !canEdit}
            >
              <SelectTrigger
                id={`disp-sub-${ticket.id}`}
                className={cn('h-9 text-xs', !reasonHasChildren && 'opacity-50')}
              >
                <SelectValue
                  placeholder={
                    !draft.reasonId
                      ? 'Pick a reason first'
                      : reasonHasChildren
                        ? 'Select sub-reason'
                        : 'No sub-reasons'
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {draft.subReasonId != null ? (
                  <SelectItem value={CLEAR_SELECT_VALUE}>— Clear —</SelectItem>
                ) : null}
                {subReasonOptions.map((r) => (
                  <SelectItem key={r.id} value={String(r.id)}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>

        <div className="grid gap-3 md:grid-cols-2">
          <Field label="Is avoidable?" htmlFor={`disp-avoid-${ticket.id}`}>
            <Select
              value={
                draft.isAvoidable === '' ? AVOIDABLE_NOT_SET_SELECT_VALUE : draft.isAvoidable
              }
              onValueChange={(v) =>
                setDraft((d) => ({
                  ...d,
                  isAvoidable:
                    v === AVOIDABLE_NOT_SET_SELECT_VALUE
                      ? ''
                      : (v as Exclude<CrmTicketAvoidable, ''>),
                }))
              }
              disabled={!canEdit}
            >
              <SelectTrigger id={`disp-avoid-${ticket.id}`} className="h-9 text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {AVOIDABLE_OPTIONS.map((o) => (
                  <SelectItem key={o.selectValue} value={o.selectValue}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {suggestion && suggestion !== draft.isAvoidable ? (
              <SuggestionRow hint={suggestion} onApply={applySuggestion} disabled={!canEdit} />
            ) : null}
          </Field>
          <Field
            label="Notes"
            htmlFor={`disp-notes-${ticket.id}`}
            hint="Free text — separate from the resolution note."
          >
            <textarea
              id={`disp-notes-${ticket.id}`}
              value={draft.notes}
              onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
              rows={3}
              disabled={!canEdit}
              placeholder="What did the offender say? Was the SOP followed? Anything to escalate?"
              className="w-full rounded-md border border-input bg-surface px-3 py-2 text-xs disabled:opacity-60"
            />
          </Field>
        </div>

        <div className="flex items-center justify-end gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setDraft(server);
              setErrors({});
            }}
            disabled={!dirty || patch.isPending}
            className="gap-1"
          >
            <Undo2 className="h-3.5 w-3.5" /> Cancel
          </Button>
          <Button
            size="sm"
            onClick={handleSave}
            disabled={!canEdit || !dirty || patch.isPending}
            className="gap-1"
          >
            {patch.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Save className="h-3.5 w-3.5" />
            )}
            Save disposition
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

function SuggestionRow({
  hint,
  onApply,
  disabled,
}: {
  hint: string;
  onApply: () => void;
  disabled?: boolean;
}) {
  const label = hint === 'yes' ? 'Avoidable' : hint === 'no' ? 'Unavoidable' : 'Unknown';
  return (
    <p className="mt-1 inline-flex items-center gap-1 text-[11px] text-muted-foreground">
      <Wand2 className="h-3 w-3" /> Suggested: <span className="font-medium">{label}</span>
      <button
        type="button"
        onClick={onApply}
        disabled={disabled}
        className="press rounded-sm px-1 text-primary hover:underline focus-ring disabled:opacity-50"
      >
        Apply
      </button>
    </p>
  );
}

